import { Table, Tooltip } from 'antd'
import { SortOrder } from 'antd/lib/table'
import { FC, useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import PerButtonCircle from '../../../../components/custom_components/ButtonCircle'
import { PerEmpty } from '../../../../components/custom_components/Empty'
import { PerTableActionButtons } from '../../../../components/custom_components/TableActionButtons'
import { tableActionButtons } from '../../../../components/custom_components/TableActionButtons/tableActionButtons.uiprops'
import PerText from '../../../../components/custom_components/Text'
import { useAuth } from '../../../../context/auth-context'
import { useHrManageMeeting } from '../../../../graphql/meeting/hooks/useHrManageMeeting'
import { useDateTime } from '../../../../shared/useDateTime'
import {
  AppraisalWithFeedbackDto,
  Country,
  EmployeeDisplayTinyInfo,
  HrManageMeetingInput,
  MeetingHrFeedbackResult,
  MeetingPdfStatus,
  MeetingStatus,
} from '../../../../types'
import { getConclusionHrefByMeeting } from '../../../meeting/util/hrefUtils'
import { DisputedAppraisalFeedbackModal } from '../../appraisal/DisputedAppraisalFeedbackModal'
import { appraisalTableUiProps } from '../../appraisal/appraisalTable.uiprops'
import { useHrSupportStore } from '../HrSupportStore'
import shallow from 'zustand/shallow'
import { PerAvatarPlus } from '../../../../components/custom_components/AvatarPlus'
import { NameDisplayPreference } from '../../../../graphql/enum/name-display-preference.enum'
import { ApolloError } from '@apollo/client'
import { ConfirmedAppraisalFeedbackModal } from '../../appraisal/ConfirmedAppraisalFeedbackModal'
import classNames from 'classnames'
import { showNotification } from '../../../../components/custom_components/Notification/notification'
import { NewLoadingAndErrors } from '../../../../components/custom_components/LoadingAndErrors/NewLoadingAndErrors'
import BackgroundBox from '../../../../components/custom_components/BackgroundBox'

interface Props {
  filteredData?: AppraisalWithFeedbackDto[]
  feedbackToResolveCounter?: number
  isLoading: boolean
  isLoadingMore: boolean
  error?: ApolloError
  showMoreButtonVisible: boolean
}

export const HrSupportFeedbackTable: FC<Props> = ({
  filteredData,
  feedbackToResolveCounter,
  isLoading,
  isLoadingMore,
  error,
  showMoreButtonVisible,
}) => {
  const { t } = useTranslation()
  const { user } = useAuth()
  const { formatDateShort, formatDateTimeShort } = useDateTime(user)
  const { feedbackBtnColor, feedbackBtnTooltip, pdfBtnTooltip } = appraisalTableUiProps(t, formatDateShort, formatDateTimeShort)
  const [selected, filters] = useHrSupportStore((state) => [state.selected, state.feedbackTabFilters], shallow)
  const { hrManageMeeting } = useHrManageMeeting()
  const [open, setOpen] = useState(false)
  const [modalData, setModalData] = useState<AppraisalWithFeedbackDto | undefined>(undefined)
  const [hrComment, setHrComment] = useState<string | undefined>(undefined)
  const [hrResult, setHrResult] = useState<MeetingHrFeedbackResult>(MeetingHrFeedbackResult.NoResult)
  const [saveCommentInProgress, setSaveCommentInProgress] = useState<boolean>(false)
  const [resolveFeedbackInProgress, setResolveFeedbackInProgress] = useState<boolean>(false)
  const [tableData, setTableData] = useState<AppraisalWithFeedbackDto[]>(filteredData || [])
  const [modalError, setModalError] = useState<string | undefined>(undefined)

  const { tableActionButtonsRowWidth } = tableActionButtons()

  const onFeedbackModalOpen = (rowData: AppraisalWithFeedbackDto) => {
    setModalData(rowData)
    setHrComment(rowData && rowData.hrDisputeComment ? rowData.hrDisputeComment : undefined)
    setHrResult(rowData ? rowData.hrResult : MeetingHrFeedbackResult.NoResult)
    setOpen(true)
  }
  const onFeedbackModalCancel = () => {
    setOpen(false)
  }

  useEffect(() => {
    if (filteredData) {
      setTableData(filteredData)
    }
  }, [filteredData, setTableData])

  if ((isLoading && !isLoadingMore) || error || !filteredData || feedbackToResolveCounter === undefined) {
    return (
      <BackgroundBox r height="withTabsAndInfoBox">
        <NewLoadingAndErrors isLoading={isLoading && !isLoadingMore} hasNoData={!filteredData} error={error} t={t} showSpinner />
      </BackgroundBox>
    )
  }

  const onFeedbackModalSubmit = async (formData: { employeeId: string; hrManageMeetingInput: HrManageMeetingInput }) => {
    if (modalData && selected && !!selected.value) {
      setResolveFeedbackInProgress(true)
      const res = await hrManageMeeting(formData.hrManageMeetingInput, selected.value as Country, filters, {})
      if (!!res.errors && !!res.errors.length) {
        setModalError(t('global.notifications.error.description'))
      } else {
        const meetingResolution = modalData.status === MeetingStatus.Confirmed ? 'readAndConfirm' : 'resolve'
        const meetingResolutionType =
          modalData.status !== MeetingStatus.Confirmed
            ? formData.hrManageMeetingInput.hrResult === MeetingHrFeedbackResult.KeepingChanges
              ? 'Keeping changes'
              : 'Appraisal update'
            : undefined
        setResolveFeedbackInProgress(false)
        showNotification({
          type: 'success',
          message: t(`companySettingsView.feedbackTab.table.notifications.${meetingResolution}.success.title`),
          description: t(`companySettingsView.feedbackTab.table.notifications.${meetingResolution}.success.description`, {
            meetingResolutionType,
          }),
        })
        setModalError(undefined)
        setOpen(false)
      }
    }
  }

  const saveComment = async (formData: { employeeId: string; hrManageMeetingInput: HrManageMeetingInput }) => {
    if (selected && !!selected.value) {
      setSaveCommentInProgress(true)
      const res = await hrManageMeeting(formData.hrManageMeetingInput, selected.value as Country, filters)

      if (!res.errors) {
        setSaveCommentInProgress(false)
        const newFilteredData = filteredData.map((m) => {
          if (m.id === formData.hrManageMeetingInput.meetingId) {
            m.hrDisputeComment = formData.hrManageMeetingInput.hrDisputeComment
          }
          return m
        })
        setTableData(newFilteredData)
      }
    }
  }

  const employeeTableHeader = (
    <Tooltip title={t('employeeList.table.employeeTooltip')}>{t('companySettingsView.feedbackTab.table.heading.employee')}</Tooltip>
  )

  const columns = [
    {
      key: 'employee',
      className: 'minWidth-column-l',
      title: employeeTableHeader,
      defaultSortOrder: 'ascend' as SortOrder,
      render: (data: AppraisalWithFeedbackDto) => {
        return (
          <PerAvatarPlus
            nameDisplayPreference={user.nameDisplayPreference as NameDisplayPreference}
            employeeId={data.replyer.id}
            firstName={data.replyer.firstName}
            lastName={data.replyer.lastName}
            linkToProfile={
              data.replyer.id !== user.employeeId
                ? {
                    resigned: false,
                  }
                : undefined
            }
            tooltip={data.replyer.id !== user.employeeId ? t('employeeList.table.viewProfile') : undefined}
          />
        )
      },
    },
    {
      key: 'company',
      className: 'minWidth-column-l',
      title: t('companySettingsView.feedbackTab.table.heading.company'),
      render: (data: AppraisalWithFeedbackDto) => <PerText>{data.companyName || '-'}</PerText>,
    },
    {
      key: 'appraisalSeason',
      className: 'minWidth-column-s',
      title: t('companySettingsView.feedbackTab.table.heading.appraisalSeason'),
      render: (rowData: AppraisalWithFeedbackDto) => <PerText>{rowData.appraisalSeason.year}</PerText>,
    },
    {
      key: 'companyConfirmation',
      className: 'minWidth-column-l direction-column',
      title: t('companySettingsView.appraisalTab.table.companyConfirmation.heading'),
      render: (rowData: AppraisalWithFeedbackDto) => {
        const renderBy = (type: 'reviewedBy' | 'discussedWith', by: EmployeeDisplayTinyInfo) => {
          return (
            <PerText fontSize="xs" color="secondary">
              {`${t(`companySettingsView.appraisalTab.table.companyConfirmation.${type}`)} ${
                by && by.firstName && by.lastName ? `${by.firstName} ${by.lastName}` : t('user.unknown.name')
              }`}
            </PerText>
          )
        }
        return (
          <div style={{ flexDirection: 'column', justifyContent: 'center' }}>
            <>
              <PerText mb="xxxxs">{formatDateShort(rowData.managerConfirmationDate)}</PerText>
              {renderBy('reviewedBy', rowData.reviewer)}
              {rowData.discussedWith && renderBy('discussedWith', rowData.discussedWith)}
            </>
          </div>
        )
      },
    },
    {
      key: 'employeeConfirmation',
      className: 'minWidth-column-l',
      title: t('companySettingsView.appraisalTab.table.employeeConfirmation.heading'),
      render: (rowData: AppraisalWithFeedbackDto) => <PerText>{formatDateShort(rowData.employeeConfirmationDate)}</PerText>,
    },
    {
      key: 'hrFeedback',
      className: 'minWidth-column-s textAlign-center',
      title: t('companySettingsView.feedbackTab.table.heading.feedback'),
      render: (rowData: AppraisalWithFeedbackDto) => (
        <>
          {rowData.hrFeedback && (
            <PerButtonCircle
              color={feedbackBtnColor(rowData.status, rowData.hrResult)}
              variant="pastel"
              icon="warning_fill"
              tooltip={feedbackBtnTooltip(rowData.status, rowData.hrResult)}
              onClick={() => onFeedbackModalOpen(rowData)}
            />
          )}
        </>
      ),
    },
    {
      key: 'resolution',
      className: 'minWidth-column-m',
      title: t('companySettingsView.feedbackTab.table.heading.resolution'),
      render: (rowData: AppraisalWithFeedbackDto) => (
        <PerText>
          {rowData.hrResult === MeetingHrFeedbackResult.NoResult
            ? t('companySettingsView.feedbackTab.table.toResolve')
            : rowData.hrResult === MeetingHrFeedbackResult.Resolved
            ? t('companySettingsView.feedbackTab.table.read')
            : rowData.hrResult.replace(/([a-z])([A-Z])/g, '$1 $2')}
        </PerText>
      ),
    },
    {
      key: 'actions',
      width: tableActionButtonsRowWidth(1),
      title: t('companySettingsView.feedbackTab.table.heading.actions'),
      className: 'textAlign-left minWidth-column-s',
      render: (rowData: AppraisalWithFeedbackDto) => {
        const conclusionPdfStatus = rowData.conclusionPdf && rowData.conclusionPdf.status
        return (
          <PerTableActionButtons
            variant="pastel"
            justify="start"
            buttons={[
              {
                icon: 'view_fill',
                color: 'primary',
                tooltip: conclusionPdfStatus ? pdfBtnTooltip(conclusionPdfStatus) : undefined,
                asLink:
                  rowData.conclusionPdf && conclusionPdfStatus === MeetingPdfStatus.FinishedSuccess
                    ? getConclusionHrefByMeeting({
                        id: rowData.id,
                        conclusionPdfFileName: rowData.conclusionPdf.fileName,
                        conclusionPdfDownloadToken: rowData.conclusionPdf.downloadToken,
                        conclusionPdfStatus: rowData.conclusionPdf.status,
                      })
                    : undefined,
                disabled: !(conclusionPdfStatus === MeetingPdfStatus.FinishedSuccess),
              },
            ]}
          />
        )
      },
    },
  ]

  return (
    <>
      {modalData && modalData.status === MeetingStatus.DisputedConfirmed && (
        <DisputedAppraisalFeedbackModal
          open={open}
          closeModal={onFeedbackModalCancel}
          saveComment={async (formData) => {
            await saveComment(formData)
          }}
          onSubmit={async (formData) => {
            await onFeedbackModalSubmit(formData)
          }}
          modalData={modalData}
          modalError={modalError}
          saveCommentInProgress={saveCommentInProgress}
          resolveFeedbackInProgress={resolveFeedbackInProgress}
          hrComment={hrComment}
          hrResult={hrResult}
          setHrComment={setHrComment}
          setHrResult={setHrResult}
        />
      )}
      {modalData && modalData.status === MeetingStatus.Confirmed && (
        <ConfirmedAppraisalFeedbackModal
          resolveFeedbackInProgress={resolveFeedbackInProgress}
          open={open}
          onCancel={onFeedbackModalCancel}
          onSubmit={async (formData) => {
            await onFeedbackModalSubmit(formData)
          }}
          modalData={modalData}
          modalError={modalError}
        />
      )}
      <Table
        className={classNames('light small scroll-compensation', {
          viewWithTabsAndInfoBoxAndShowMoreButton: showMoreButtonVisible,
          viewWithTabsAndInfoBoxOneLineHeader: !showMoreButtonVisible,
        })}
        dataSource={tableData.map((o) => ({ ...o, key: o.id }))}
        columns={columns}
        rowClassName={() => 'minHeight-row'}
        scroll={{ y: true }}
        pagination={false}
        locale={{
          emptyText: <PerEmpty heading={t('companySettingsView.feedbackTab.table.emptyTable')} icon />,
        }}
      />
    </>
  )
}
